/**
 * HistoryStore - MobX store for cloud history management
 *
 * Manages conversation and search history from cloud backend (DynamoDB + S3).
 * Provides observable state for UI components with caching and pagination.
 *
 * Key features:
 * - Load conversations and searches from cloud
 * - Pagination support for large history lists
 * - Local caching to minimize API calls
 * - Optimistic updates for better UX
 * - Offline detection and graceful degradation
 * - Singleton pattern for global access
 */

import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import * as historyApi from '../queries/history';
import Analytics from "../lib/Analytics";

// Fields only the backend writes on a conversation (PLEX-6087 S2): a reload takes them from the
// server even when a local save of the item is still in flight.
const SERVER_OWNED_FIELDS = ["inProgress", "streamId", "lastEventId"];

class HistoryStore {
  // Observable state
  conversations = [];
  searches = [];
  projects = [];
  selectedIds = new Set();
  loading = false;
  error = null;
  nextCursor = null;
  hasMore = false;
  isOnline = navigator.onLine;

  // Cache for individual items with full data
  conversationCache = new Map();
  searchCache = new Map();

  // Per-item promise queue: ensures operations on the same ID are serialized
  // (prevents UPDATE racing ahead of CREATE for a new conversation)
  _pendingById = new Map();

  // Promise cache keyed by `${type}:${limit}`. Multiple components mounting at
  // once (WorkspaceHome + ProgramHome + ChatListRail) used to each fire their
  // own /api/history GET; the in-flight guard collapses them into one.
  _inflightLoadHistory = new Map();

  // Promise cache for getConversation(id). The v2 chat loader re-runs on auth
  // transitions and on prop changes before the first response lands, which
  // produced duplicate GET /api/history/<id>?include_turns=true calls. The
  // in-flight guard hands the same promise back until the request settles
  // (after which the conversationCache takes over). Entries are {promise, fresh};
  // _getConversationSeq numbers each id's requests so an older answer that lands
  // last doesn't overwrite the cache.
  _inflightGetConversation = new Map();
  _getConversationSeq = new Map();

  // User store reference (injected via constructor)
  userStore = null;

  constructor(userStore = null) {
    this.userStore = userStore;

    makeObservable(this, {
      conversations: observable,
      searches: observable,
      projects: observable,
      selectedIds: observable,
      loading: observable,
      error: observable,
      nextCursor: observable,
      hasMore: observable,
      isOnline: observable,
      sortedConversations: computed,
      sortedSearches: computed,
      sortedProjects: computed,
      loadHistory: action,
      loadMore: action,
      getConversation: action,
      getSearch: action,
      saveConversation: action,
      saveSearch: action,
      saveProject: action,
      updateConversation: action,
      updateSearch: action,
      updateProject: action,
      deleteItem: action,
      deleteProject: action,
      patchConversationContext: action,
      patchSearchContext: action,
      setSelectedIds: action,
      clearAllHistory: action,
      clearHistoryByType: action,
      clearError: action,
      setOnline: action,
      setUserStore: action,
      refreshOnFocus: action,
    });

    // Listen for online/offline events
    window.addEventListener('online', () => this.setOnline(true));
    window.addEventListener('offline', () => this.setOnline(false));

    // Tab visibility refresh is triggered by the HistoryDrawer component
    // (which knows the active mode) via refreshOnFocus(type).
  }

  /**
   * Set user store reference for authentication (for backward compatibility)
   * @deprecated Use constructor injection instead
   */
  setUserStore(userStore) {
    this.userStore = userStore;
  }

  /**
   * Update online status
   */
  setOnline(online) {
    this.isOnline = online;
    if (online && this.error) {
      // Clear errors when back online
      this.clearError();
    }
  }

  /**
   * Projects sorted alphabetically by title (case-insensitive)
   */
  get sortedProjects() {
    return this.projects.slice().sort((a, b) => {
      const aTitle = (a.context?.title || '').toLowerCase();
      const bTitle = (b.context?.title || '').toLowerCase();
      return aTitle.localeCompare(bTitle);
    });
  }

  /**
   * Conversations sorted alphabetically by title (case-insensitive)
   */
  get sortedConversations() {
    return this.conversations.slice().sort((a, b) => {
      const aTitle = (a.context?.title || '').toLowerCase();
      const bTitle = (b.context?.title || '').toLowerCase();
      return aTitle.localeCompare(bTitle);
    });
  }

  /**
   * Searches sorted alphabetically by title (case-insensitive)
   */
  get sortedSearches() {
    return this.searches.slice().sort((a, b) => {
      const aTitle = (a.context?.title || '').toLowerCase();
      const bTitle = (b.context?.title || '').toLowerCase();
      return aTitle.localeCompare(bTitle);
    });
  }

  /**
   * Load initial history from cloud
   *
   * @param {string} type - 'ALL', 'CONVERSATION', or 'SEARCH'
   * @param {number} limit - Max items to return (default: 50)
   */
  async loadHistory(type = 'ALL', limit = 50) {
    if (!this.isOnline) {
      this.error = 'Cloud history is unavailable offline';
      return;
    }

    if (!this.userStore?.isAuthenticated) {
      this.error = 'Authentication required to load history';
      return;
    }

    // Collapse concurrent callers (parent route + child route both mounting)
    // onto a single request. Keyed by type+limit so a CONVERSATION load and a
    // PROJECT load can still run in parallel.
    const key = `${type}:${limit}`;
    const inflight = this._inflightLoadHistory.get(key);
    if (inflight) return inflight;
    const promise = this._doLoadHistory(type, limit).finally(() => {
      this._inflightLoadHistory.delete(key);
    });
    this._inflightLoadHistory.set(key, promise);
    return promise;
  }

  async _doLoadHistory(type, limit) {
    this.loading = true;
    this.error = null;

    try {
      // For CONVERSATION/SEARCH modes, fetch projects in parallel so they're
      // ready before items render. ALL mode gets projects from the same response.
      const needsProjects = type !== 'ALL' && this.projects.length === 0;
      const [result, projectsResult] = await Promise.all([
        historyApi.listHistory(this.userStore.authData, {
          type,
          limit,
          includeTurns: false,
        }),
        needsProjects
          ? historyApi.listHistory(this.userStore.authData, { type: 'PROJECT', limit: 100, includeTurns: false })
          : Promise.resolve(null),
      ]);

      runInAction(() => {
        // Set projects first so folder structure is ready when items render
        if (type === 'ALL') {
          this.projects = result.items.filter(i => i.type === 'PROJECT');
        } else if (projectsResult) {
          this.projects = projectsResult.items.filter(i => i.type === 'PROJECT');
        }

        if (type === 'ALL') {
          this.conversations = this._mergePreservingPending(
            this.conversations,
            result.items.filter(i => i.type === 'CONVERSATION')
          );
          const seenIds = new Set();
          this.searches = this._mergePreservingPending(
            this.searches,
            result.items
              .filter(i => i.type === 'SEARCH')
              .map(item => this._deserializeSearchParams(item))
              .filter(item => {
                if (seenIds.has(item.id)) return false;
                seenIds.add(item.id);
                return true;
              })
          );
        } else if (type === 'CONVERSATION') {
          this.conversations = this._mergePreservingPending(
            this.conversations,
            result.items.filter(i => i.type !== 'PROJECT')
          );
        } else if (type === 'SEARCH') {
          const seenIds = new Set();
          this.searches = this._mergePreservingPending(
            this.searches,
            result.items
              .filter(i => i.type !== 'PROJECT')
              .map(item => this._deserializeSearchParams(item))
              .filter(item => {
                if (seenIds.has(item.id)) return false;
                seenIds.add(item.id);
                return true;
              })
          );
        }
        this.nextCursor = result.nextCursor;
        this.hasMore = result.nextCursor != null;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to load history';
        this.loading = false;
        console.warn('Failed to load history:', error);
      });
      // Re-throw network errors so callers can implement retry logic
      if (error?.name === 'ServerOfflineError') throw error;
    }
  }

  /**
   * Load more items (pagination)
   *
   * @param {string} type - 'ALL', 'CONVERSATION', or 'SEARCH'
   * @param {number} limit - Max items to return (default: 50)
   */
  async loadMore(type = 'ALL', limit = 50) {
    if (!this.hasMore || this.loading || !this.isOnline) {
      return;
    }

    if (!this.userStore?.isAuthenticated) {
      return;
    }

    Analytics.event(Analytics.EVENTS.HISTORY_LOAD_MORE, { type });

    this.loading = true;

    try {
      const result = await historyApi.listHistory(this.userStore.authData, {
        type,
        limit,
        before: this.nextCursor,
        includeTurns: false,
      });

      runInAction(() => {
        // Backend already filters by type, no need to filter here
        const newItems = result.items;

        if (type === 'CONVERSATION') {
          // Deduplicate by ID before adding
          const existingIds = new Set(this.conversations.map(c => c.id));
          const uniqueNewItems = newItems.filter(c => !existingIds.has(c.id));
          this.conversations = [...this.conversations, ...uniqueNewItems];
        } else if (type === 'SEARCH') {
          // Deserialize params and deduplicate by ID before adding
          const existingIds = new Set(this.searches.map(s => s.id));
          const uniqueNewItems = newItems
            .map(item => this._deserializeSearchParams(item))
            .filter(s => !existingIds.has(s.id));
          this.searches = [...this.searches, ...uniqueNewItems];
        }

        this.nextCursor = result.nextCursor;
        this.hasMore = result.nextCursor != null;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to load more history';
        this.loading = false;
        console.warn('Failed to load more history:', error);
      });
    }
  }

  /**
   * Get full conversation with turns
   *
   * @param {string} conversationId - Conversation UUID
   * @returns {Promise<Object>} Full conversation data
   */
  async getConversation(conversationId, {fresh = false} = {}) {
    // PLEX-6303: `fresh` skips the cache, for a conversation whose running turn has just
    // ended elsewhere: the cached copy predates its result.
    if (fresh) {
      this.conversationCache.delete(conversationId);
    }
    // Check cache first
    if (this.conversationCache.has(conversationId)) {
      return this.conversationCache.get(conversationId);
    }

    if (!this.isOnline) {
      throw new Error('Cannot load conversation while offline');
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    // Collapse concurrent callers (the v2 chat loader's effect re-runs on
    // auth-ready) onto the same promise. A fresh caller joins only a fresh request:
    // a plain one already in flight may have left before the save it needs to see
    // (review on plex-search-ui #143).
    const inflight = this._inflightGetConversation.get(conversationId);
    if (inflight && (inflight.fresh || !fresh)) return inflight.promise;
    const seq = (this._getConversationSeq.get(conversationId) || 0) + 1;
    this._getConversationSeq.set(conversationId, seq);
    const entry = {fresh};
    entry.promise = (async () => {
      try {
        const conversation = await historyApi.getHistoryItem(
          this.userStore.authData,
          conversationId,
          'conversation',
          true
        );

        runInAction(() => {
          // A request a later one overtook leaves the cache to that one.
          if (this._getConversationSeq.get(conversationId) === seq) {
            this.conversationCache.set(conversationId, conversation);
          }
        });

        return conversation;
      } catch (error) {
        // 404 means the conversation doesn't exist (deleted, wrong cell, etc.) — not an error
        if (error?.status === 404) {
          return null;
        }
        // 403 means the item exists but belongs to another user — propagate so callers
        // can generate a new UUID instead of reusing the original owner's UUID
        if (error?.name === 'NotAuthorized') {
          throw error;
        }
        console.error('Failed to get conversation:', error);
        return null;
      }
    })().finally(() => {
      if (this._inflightGetConversation.get(conversationId) === entry) {
        this._inflightGetConversation.delete(conversationId);
      }
    });
    this._inflightGetConversation.set(conversationId, entry);
    return entry.promise;
  }

  /**
   * Deserialize search params from JSON string to object
   * Handles backward compatibility if params is already an object
   *
   * @param {Object} search - Search item with context
   * @returns {Object} Search with deserialized params
   */
  _deserializeSearchParams(search) {
    if (search?.context?.paramsJSON && typeof search.context.paramsJSON === 'string') {
      try {
        search.context.params = JSON.parse(search.context.paramsJSON);
        // Keep paramsJSON for reference, but params is now the usable object
      } catch (e) {
        console.warn('Failed to parse search paramsJSON:', e);
        // Leave as-is if parsing fails
      }
    }
    return search;
  }

  /**
   * Get full search with results
   *
   * @param {string} searchId - Search UUID
   * @returns {Promise<Object>} Full search data
   */
  async getSearch(searchId) {
    if (this.searchCache.has(searchId)) {
      return this.searchCache.get(searchId);
    }

    if (!this.isOnline) {
      throw new Error('Cannot load search while offline');
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    try {
      const search = await historyApi.getHistoryItem(this.userStore.authData, searchId, 'search');

      if (!search) {
        return null;
      }

      // Deserialize params from JSON
      this._deserializeSearchParams(search);

      runInAction(() => {
        this.searchCache.set(searchId, search);
      });

      return search;
    } catch (error) {
      if (error?.status !== 404 && error?.name !== 'NotAuthorized') {
        console.error('Failed to get search:', error);
      }
      throw error;
    }
  }

  /**
   * Save conversation or search to cloud (auto-detects destination from context.url)
   * - If context.url is present → SEARCH table
   * - If context.url is absent → CONVERSATION table
   *
   * For searches, checks for existing search by URL and updates if found.
   *
   * @param {Object} conversationData - Conversation/search data to save
   * @returns {Promise<Object>} Created/updated item with server-assigned ID
   */
  /**
   * Serialize async operations for the same item ID.
   * Each call chains onto the previous promise for that ID so CREATE always
   * completes before any subsequent UPDATE for the same conversation/search.
   */
  _enqueue(id, fn) {
    const prev = this._pendingById.get(id) ?? Promise.resolve();
    const next = prev.then(fn, fn); // run fn regardless of whether prev succeeded
    // Clean up once no more operations are queued for this ID. The map holds `tracked`, so
    // that's what to compare: comparing `next` never matched, and every id ever saved stayed
    // "pending" for the life of the tab (PLEX-6361).
    // The caller handles next's rejection; this copy only orders the queue, so it swallows it.
    const tracked = next
      .finally(() => {
        if (this._pendingById.get(id) === tracked) {
          this._pendingById.delete(id);
        }
      })
      .catch(() => {});
    this._pendingById.set(id, tracked);
    return next;
  }

  _mergePreservingPending(localItems, serverItems) {
    const pendingLocal = new Map();
    for (const item of localItems) {
      if (this._pendingById.has(item.id)) {
        pendingLocal.set(item.id, item);
      }
    }
    if (pendingLocal.size === 0) return serverItems;
    // A save in flight keeps the local copy, but the backend owns a conversation's liveness
    // (PLEX-6087 S2): no client save carries it, so those fields come from the server.
    return serverItems.map(item => {
      const local = pendingLocal.get(item.id);
      if (!local) return item;
      const merged = { ...local };
      for (const field of SERVER_OWNED_FIELDS) {
        if (field in item) merged[field] = item[field];
      }
      return merged;
    });
  }

  async saveConversation(conversationData) {
    return this._enqueue(conversationData.id, () => this._saveConversation(conversationData));
  }

  async _saveConversation(conversationData) {
    // Determine destination table based on context.url presence
    const isSearch = conversationData.context?.url != null;
    const type = isSearch ? 'SEARCH' : 'CONVERSATION';

    console.info('[HistoryStore.saveConversation] called', {
      id: conversationData.id,
      type,
      projectPath: conversationData.context?.projectPath,
      v2: conversationData.context?.v2,
      url: conversationData.context?.url,
      title: conversationData.context?.title,
    });

    if (!this.isOnline) {
      throw new Error(`Cannot save ${type.toLowerCase()} while offline`);
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    try {
      let result;

      if (isSearch) {
        // For searches, always create new (allow duplicates with same URL)
        console.debug('[HistoryStore.saveConversation] Creating new search');
        result = await historyApi.createHistoryItem(this.userStore.authData, 'search', conversationData);
      } else {
        // For conversations, always create new
        console.debug('[HistoryStore.saveConversation] Creating new conversation');
        result = await historyApi.createHistoryItem(this.userStore.authData, 'conversation', conversationData);
      }

      console.info('[HistoryStore.saveConversation] api success', {
        id: result?.id,
        modified: result?.modified,
        serverProjectPath: result?.context?.projectPath,
      });

      runInAction(() => {
        const newItem = {
          id: result.id,
          created: result.created || new Date().toISOString(),
          modified: result.modified,
          // Prefer context from server response (may include backend-generated title)
          context: result.context || conversationData.context,
          metadata: result.metadata,
        };

        // Add to appropriate list. Reassign instead of mutating in place —
        // observers that depend on the array reference (e.g. ChatListRail's
        // useMemo([..., historyItems, ...])) don't recompute on unshift /
        // index assignment alone, because the observable array's proxy
        // reference is stable. loadHistory/loadMore/deleteItem all
        // reassign for the same reason.
        if (isSearch) {
          const existingIndex = this.searches.findIndex(s => s.id === result.id);
          if (existingIndex >= 0) {
            console.debug('[HistoryStore.saveConversation] Updating search in list', result.id);
            this.searches = this.searches.map((s, i) => (i === existingIndex ? newItem : s));
          } else {
            console.debug('[HistoryStore.saveConversation] Adding search to list', result.id);
            this.searches = [newItem, ...this.searches];
          }
          // Do not cache here — the API response lacks turns, and caching it would
          // cause getSearch() to return incomplete data when the item is later loaded.
          console.debug('[HistoryStore.saveConversation] searches.length:', this.searches.length);
        } else {
          const existingIndex = this.conversations.findIndex(c => c.id === result.id);
          if (existingIndex >= 0) {
            console.debug('[HistoryStore.saveConversation] Updating conversation in list', result.id);
            this.conversations = this.conversations.map((c, i) => (i === existingIndex ? newItem : c));
          } else {
            console.debug('[HistoryStore.saveConversation] Adding conversation to list', result.id);
            this.conversations = [newItem, ...this.conversations];
          }
          // Cache the full conversation (using the turns we just sent) so that
          // a subsequent title-edit updateConversation doesn't hit a cache miss
          // and try to fetch from the server before the item exists there.
          this.conversationCache.set(result.id, {
            ...newItem,
            turns: conversationData.turns,
          });
          console.debug('[HistoryStore.saveConversation] conversations.length:', this.conversations.length);
        }
      });

      const listHead = (isSearch ? this.searches : this.conversations)
        .slice(0, 3)
        .map((it) => ({ id: it.id, modified: it.modified }));
      console.info('[HistoryStore.saveConversation] ok', {
        id: result?.id,
        type,
        listHead,
      });
      return result;
    } catch (error) {
      console.error(`Failed to save ${type.toLowerCase()}:`, error);
      throw error;
    }
  }

  /**
   * Save search to cloud (creates new or updates existing)
   *
   * @param {Object} searchData - Search data to save
   * @returns {Promise<Object>} Created/updated search with server-assigned ID
   */
  async saveSearch(searchData) {
    if (!this.isOnline) {
      throw new Error('Cannot save search while offline');
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    try {
      // Always create new search (allow duplicates with same URL)
      console.debug('[HistoryStore.saveSearch] Creating new search');
      const result = await historyApi.createHistoryItem(this.userStore.authData, 'search', searchData);

      runInAction(() => {
        const newItem = {
          id: result.id,
          created: result.created || new Date().toISOString(),
          modified: result.modified,
          context: searchData.context,
          metadata: result.metadata,
        };

        // Reassign instead of mutating — see saveConversation for rationale.
        const existingIndex = this.searches.findIndex(s => s.id === result.id);
        if (existingIndex >= 0) {
          this.searches = this.searches.map((s, i) => (i === existingIndex ? newItem : s));
        } else {
          this.searches = [newItem, ...this.searches];
        }
        // Do not cache here — the API response lacks turns, and caching it would
        // cause getSearch() to return incomplete data when the item is later loaded.
      });

      return result;
    } catch (error) {
      console.error('Failed to save search:', error);
      throw error;
    }
  }

  /**
   * Save a project to cloud
   *
   * @param {Object} projectData - Project data to save
   * @returns {Promise<Object>} Created project with server-assigned ID
   */
  async saveProject(projectData) {
    const result = await historyApi.createHistoryItem(
      this.userStore.authData, 'project',
      { ...projectData, type: 'PROJECT' }
    );
    runInAction(() => {
      this.projects.push(result);
    });
    return result;
  }

  /**
   * Update existing project
   *
   * @param {string} projectId - Project UUID
   * @param {Object} updateData - Updated project data
   */
  async updateProject(projectId, updateData) {
    // Optimistic update
    const idx = this.projects.findIndex(p => p.id === projectId);
    const original = idx !== -1 ? this.projects[idx] : null;
    if (idx !== -1) {
      this.projects[idx] = {
        ...this.projects[idx],
        ...updateData,
        context: { ...this.projects[idx].context, ...updateData.context },
      };
    }
    try {
      await historyApi.updateHistoryItem(
        this.userStore.authData, projectId, 'project', updateData
      );
    } catch (err) {
      // Roll back
      if (original) {
        runInAction(() => { this.projects[idx] = original; });
      }
      throw err;
    }
  }

  /**
   * Delete a project
   *
   * @param {string} projectId - Project UUID
   */
  async deleteProject(projectId) {
    // Optimistic: remove immediately
    const removed = this.projects.find(p => p.id === projectId);
    this.projects = this.projects.filter(p => p.id !== projectId);
    try {
      await historyApi.deleteHistoryItem(
        this.userStore.authData, projectId, 'project'
      );
    } catch (err) {
      // Roll back
      if (removed) {
        runInAction(() => { this.projects = [...this.projects, removed]; });
      }
      throw err;
    }
  }

  /** Immediately patch a conversation's context in local state (for optimistic updates). */
  patchConversationContext(id, context) {
    const index = this.conversations.findIndex(c => c.id === id);
    if (index >= 0) {
      this.conversations = this.conversations.map((c, i) =>
        i === index ? { ...c, context } : c
      );
    }
  }

  /** Immediately patch a search's context in local state (for optimistic updates). */
  patchSearchContext(id, context) {
    const index = this.searches.findIndex(s => s.id === id);
    if (index >= 0) {
      this.searches = this.searches.map((s, i) =>
        i === index ? { ...s, context } : s
      );
    }
  }

  /**
   * Update the set of selected item IDs (for multi-select)
   *
   * @param {string[]} ids - Array of selected item IDs
   */
  setSelectedIds(ids) {
    this.selectedIds = new Set(ids);
  }

  /**
   * Update existing conversation or search (auto-detects destination from context.url)
   * - If context.url is present → updates SEARCH table
   * - If context.url is absent → updates CONVERSATION table
   *
   * @param {string} conversationId - Conversation/search ID
   * @param {Object} conversationData - Updated data
   * @returns {Promise<Object>} Updated item
   */
  async updateConversation(conversationId, conversationData) {
    return this._enqueue(conversationId, () => this._updateConversation(conversationId, conversationData));
  }

  async _updateConversation(conversationId, conversationData) {
    // Determine destination table based on context.url presence
    const isSearch = conversationData.context?.url != null;
    const type = isSearch ? 'SEARCH' : 'CONVERSATION';

    console.info('[HistoryStore.updateConversation] called', {
      id: conversationId,
      type,
      projectPath: conversationData.context?.projectPath,
      v2: conversationData.context?.v2,
      url: conversationData.context?.url,
    });

    if (!this.isOnline) {
      throw new Error(`Cannot update ${type.toLowerCase()} while offline`);
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    try {
      // The PUT endpoint requires turns for conversations. If the caller is doing a
      // context-only update (e.g. title rename), supply turns from cache or server.
      let payload = conversationData;
      if (!isSearch && !conversationData.turns) {
        const cached = this.conversationCache.get(conversationId);
        if (cached?.turns) {
          payload = { ...conversationData, turns: cached.turns };
        } else {
          // Cache miss — fetch full conversation to get turns
          const full = await this.getConversation(conversationId);
          if (full?.turns) {
            payload = { ...conversationData, turns: full.turns };
          }
        }
      }

      const result = await historyApi.updateHistoryItem(
        this.userStore.authData,
        conversationId,
        isSearch ? 'search' : 'conversation',
        isSearch ? conversationData : payload
      );

      runInAction(() => {
        // Reassign list arrays — see saveConversation for rationale.
        if (isSearch) {
          const index = this.searches.findIndex(s => s.id === conversationId);
          if (index >= 0) {
            this.searches = this.searches.map((s, i) =>
              i === index
                ? {
                    ...s,
                    context: conversationData.context,
                    metadata: conversationData.metadata,
                    modified: result.modified || new Date().toISOString(),
                  }
                : s
            );
          }
          // Patch cached entry's metadata only — the PUT response lacks turns so we
          // must not replace the full cache entry (that would break subsequent loads).
          if (this.searchCache.has(conversationId)) {
            const cached = this.searchCache.get(conversationId);
            this.searchCache.set(conversationId, {
              ...cached,
              context: conversationData.context,
              metadata: result.metadata ?? conversationData.metadata,
              modified: result.modified || cached.modified,
            });
          }
        } else {
          const index = this.conversations.findIndex(c => c.id === conversationId);
          if (index >= 0) {
            this.conversations = this.conversations.map((c, i) =>
              i === index
                ? {
                    ...c,
                    context: conversationData.context,
                    metadata: conversationData.metadata,
                    modified: result.modified || new Date().toISOString(),
                  }
                : c
            );
          }
          // Patch cached entry's metadata only — the PUT response lacks turns so we
          // must not replace the full cache entry (that would break subsequent loads).
          // PLEX-6693: but keep the turns this save sent, so a later update that sends none (a
          // rename) doesn't fill in the turns the chat had when it was first loaded.
          if (this.conversationCache.has(conversationId)) {
            const cached = this.conversationCache.get(conversationId);
            this.conversationCache.set(conversationId, {
              ...cached,
              ...(Array.isArray(conversationData.turns) ? { turns: conversationData.turns } : {}),
              context: conversationData.context,
              metadata: result.metadata ?? conversationData.metadata,
              modified: result.modified || cached.modified,
            });
          }
        }
      });

      // Visibility — show what came back + the resulting head of the list so
      // sort/reorder issues are obvious in the console without needing a
      // breakpoint. Trim to top 3 to keep the log line readable.
      const listHead = (isSearch ? this.searches : this.conversations)
        .slice(0, 3)
        .map((it) => ({ id: it.id, modified: it.modified }));
      console.info('[HistoryStore.updateConversation] ok', {
        id: conversationId,
        type,
        resultModified: result?.modified,
        serverProjectPath: result?.context?.projectPath,
        listHead,
      });

      return result;
    } catch (error) {
      // If the conversation/search no longer exists on the server, evict it from local state
      // rather than propagating the error. Handles TTL expiry, cross-tab deletions, etc.
      const isNotFound = error?.status === 404 ||
        (error?.status === 500 && error?.message?.toLowerCase().includes('not found'));
      if (isNotFound) {
        console.info(`[HistoryStore.updateConversation] ${type} ${conversationId} not found on server, evicting from local state`);
        runInAction(() => {
          if (isSearch) {
            this.searches = this.searches.filter(s => s.id !== conversationId);
            this.searchCache.delete(conversationId);
          } else {
            this.conversations = this.conversations.filter(c => c.id !== conversationId);
            this.conversationCache.delete(conversationId);
          }
        });
        return null;
      }
      // ServerOfflineError is transient (server temporarily unavailable); debug instead of error
      // to avoid creating Sentry issues for expected connectivity conditions (WEBAPP-K6, PLEX-6370)
      if (error?.name === 'ServerOfflineError') {
        console.debug(`[HistoryStore.updateConversation] Server offline during ${type} update, skipping`);
        return null;
      }
      console.error('Failed to update conversation:', error);
      throw error;
    }
  }

  /**
   * Update existing search
   *
   * @param {string} searchId - Search UUID or URL
   * @param {Object} searchData - Updated search data
   * @returns {Promise<Object>} Updated search
   */
  async updateSearch(searchId, searchData) {
    return this._enqueue(searchId, () => this._updateSearch(searchId, searchData));
  }

  async _updateSearch(searchId, searchData) {
    if (!this.isOnline) {
      throw new Error('Cannot update search while offline');
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    try {
      const result = await historyApi.updateHistoryItem(
        this.userStore.authData,
        searchId,
        'search',
        searchData
      );

      runInAction(() => {
        // Reassign — see saveConversation for rationale.
        const index = this.searches.findIndex(s => s.id === searchId);
        if (index >= 0) {
          this.searches = this.searches.map((s, i) =>
            i === index
              ? {
                  ...s,
                  modified: result.modified || new Date().toISOString(),
                  context: { ...s.context, ...searchData.context },
                }
              : s
          );
        }

        // Update cache
        if (this.searchCache.has(searchId)) {
          const cached = this.searchCache.get(searchId);
          this.searchCache.set(searchId, {
            ...cached,
            ...result
          });
        }
      });

      return result;
    } catch (error) {
      console.error('Failed to update search:', error);
      throw error;
    }
  }

  /**
   * Delete history item (soft delete)
   *
   * @param {string} itemId - Item UUID
   * @param {string} itemType - 'CONVERSATION' or 'SEARCH'
   */
  async deleteItem(itemId, itemType) {
    if (!this.isOnline) {
      throw new Error('Cannot delete history while offline');
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    // Optimistic delete - remove from local state FIRST to prevent race condition
    // where a save might re-add the item before the delete completes
    runInAction(() => {
      if (itemType === 'CONVERSATION') {
        this.conversations = this.conversations.filter(c => c.id !== itemId);
        this.conversationCache.delete(itemId);
      } else if (itemType === 'SEARCH') {
        this.searches = this.searches.filter(s => s.id !== itemId);
        this.searchCache.delete(itemId);
      }
    });

    try {
      const apiType = itemType === 'CONVERSATION' ? 'conversation' : 'search';
      await historyApi.deleteHistoryItem(this.userStore.authData, itemId, apiType);
      Analytics.event(Analytics.EVENTS.HISTORY_DELETE, { item_id: itemId, item_type: itemType });
    } catch (error) {
      // If 404, item already deleted on server, so optimistic delete was correct
      if (error.status === 404) {
        console.warn('Item already deleted on server:', itemId);
        return;
      }

      // For other errors, restore the item (rollback optimistic delete)
      console.error('Failed to delete item, rolling back:', error);
      // TODO: Could restore item here if we saved it before deleting
      throw error;
    }
  }

  /**
   * Clear all history items (delete all conversations and searches)
   */
  async clearAllHistory() {
    if (!this.isOnline) {
      throw new Error('Cannot clear history while offline');
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    try {
      await Promise.all([
        historyApi.deleteAllHistoryByType(this.userStore.authData, 'conversation'),
        historyApi.deleteAllHistoryByType(this.userStore.authData, 'search'),
      ]);

      runInAction(() => {
        this.conversations = [];
        this.searches = [];
        this.conversationCache.clear();
        this.searchCache.clear();
      });
    } catch (error) {
      console.error('Failed to clear history:', error);
      throw error;
    }
  }

  /**
   * Clear history items of a specific type (conversations or searches only)
   * @param {string} type - 'conversation' or 'search' (matches HistoryDrawer mode)
   */
  async clearHistoryByType(type) {
    if (!this.isOnline) {
      throw new Error('Cannot clear history while offline');
    }

    if (!this.userStore?.isAuthenticated) {
      throw new Error('Authentication required');
    }

    try {
      await historyApi.deleteAllHistoryByType(this.userStore.authData, type);

      runInAction(() => {
        if (type === 'conversation') {
          this.conversations = [];
          this.conversationCache.clear();
        } else {
          this.searches = [];
          this.searchCache.clear();
        }
      });
    } catch (error) {
      console.error(`Failed to clear ${type} history:`, error);
      throw error;
    }
  }

  /**
   * Clear error state
   */
  clearError() {
    this.error = null;
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.conversationCache.clear();
    this.searchCache.clear();
  }

  /**
   * Remove duplicate search items (keep most recent per URL)
   * Duplicates occur when multiple searches with same URL have different UUIDs
   *
   * Can be called from browser console for one-time cleanup:
   *   window.__rootStore.historyStore.removeDuplicateSearches()
   *
   * @returns {Promise<{deleted: number, kept: number}>} Statistics
   */
  async removeDuplicateSearches() {
    if (!this.isOnline || !this.userStore?.isAuthenticated) {
      throw new Error('Cannot remove duplicates while offline or not authenticated');
    }

    // Group searches by URL
    const searchesByUrl = new Map();
    for (const search of this.searches) {
      const url = search.context?.url;
      if (!url) continue;

      if (!searchesByUrl.has(url)) {
        searchesByUrl.set(url, []);
      }
      searchesByUrl.get(url).push(search);
    }

    // Find duplicates - keep most recent (by modified timestamp)
    const toDelete = [];
    for (const [url, searches] of searchesByUrl.entries()) {
      if (searches.length <= 1) continue;

      // Sort by modified timestamp (most recent first)
      searches.sort((a, b) => {
        const aTime = new Date(a.modified || a.created || 0).getTime();
        const bTime = new Date(b.modified || b.created || 0).getTime();
        return bTime - aTime;
      });

      // Keep first (most recent), delete rest
      const [keep, ...duplicates] = searches;
      console.debug(`[removeDuplicateSearches] URL ${url}: keeping ${keep.id}, deleting ${duplicates.length} duplicates`);
      toDelete.push(...duplicates.map(s => s.id));
    }

    console.log(`[removeDuplicateSearches] Found ${toDelete.length} duplicates to delete`);

    // Delete duplicates
    let deleted = 0;
    for (const itemId of toDelete) {
      try {
        await this.deleteItem(itemId, 'SEARCH');
        deleted++;
      } catch (error) {
        console.error(`Failed to delete duplicate ${itemId}:`, error);
      }
    }

    // Refresh to show cleaned list
    await this.loadHistory('SEARCH', 50);

    const kept = searchesByUrl.size;
    console.log(`[removeDuplicateSearches] Complete: deleted ${deleted}, kept ${kept} unique searches`);

    return { deleted, kept };
  }

  /**
   * Refresh history when tab becomes visible
   * This ensures users see updated history from other tabs/windows
   */
  refreshOnFocus(type = 'ALL') {
    if (document.hidden) return;
    if (!this.isOnline || !this.userStore?.isAuthenticated) return;
    if (this.loading) return;
    if (this.conversations.length === 0 && this.searches.length === 0) return;

    console.debug('[HistoryStore] Tab became visible, refreshing history type=' + type);
    this._loadHistoryWithRetry(type, 50);
  }

  _loadHistoryWithRetry(type, limit, attempt = 0) {
    const MAX_ATTEMPTS = 3;
    const BASE_DELAY_MS = 2000;
    this.loadHistory(type, limit).catch(err => {
      if (err?.name === 'ServerOfflineError' && attempt < MAX_ATTEMPTS - 1) {
        const delay = BASE_DELAY_MS * Math.pow(2, attempt);
        console.debug(`[HistoryStore] Network error on focus refresh, retrying in ${delay}ms (attempt ${attempt + 1})`);
        setTimeout(() => {
          if (!document.hidden && this.isOnline && this.userStore?.isAuthenticated) {
            this._loadHistoryWithRetry(type, limit, attempt + 1);
          }
        }, delay);
      } else {
        console.warn('Failed to refresh history on focus:', err);
      }
    });
  }
}

export default HistoryStore;